<script setup lang="ts">
  import { useGetMetersByBillingAccountId } from "@/api/account/account"
  import {
    useCancelSwitchReasons,
    useCancelSwitchV2,
  } from "@/api/change-of-tenancy/change-of-tenancy"
  import { SoChannel } from "@/api/model"
  import { useGetAccountId } from "@/lib/useGetAccountId"
  import { toTypedSchema } from "@vee-validate/zod"
  import { useForm } from "vee-validate"
  import { computed, ref, watch } from "vue"
  import * as z from "zod"
  import { Alert, AlertDescription } from "@/components/ui/alert"
  import { Button } from "@/components/ui/button"
  import {
    DialogClose,
    DialogFooter,
    DialogHeader,
    DialogTitle,
  } from "@/components/ui/dialog"
  import Select from "@/components/ui/select/Select.vue"

  import { cancellationReasons, suppliers } from "./switchCancellationContent"

  const accountIdQuery = useGetAccountId()
  const metersQuery = useGetMetersByBillingAccountId(accountIdQuery.accountId)

  defineProps<{
    success: boolean
  }>()
  const emit = defineEmits<{
    "update:success": [value: boolean]
  }>()

  const formSchema = toTypedSchema(
    z.object({
      primaryCancellationReason: z
        .string({
          required_error:
            "Please select a reason for cancelling before confirming",
        })
        .min(1, "Please select a reason for cancelling before confirming"),
      secondaryCancellationReason: z
        .string({
          required_error:
            "Please select a reason for cancelling before confirming",
        })
        .min(1, "Please select a reason for cancelling before confirming"),
      otherSupplier: z
        .string({
          required_error: "Please select your new supplier",
        })
        .min(1, "Please select your new supplier")
        .optional(),
    }),
  )

  const { handleSubmit } = useForm({
    validationSchema: formSchema,
  })

  const primaryChoice = ref("")
  const secondaryChoice = ref("")
  const otherSupplier = ref("")

  const secondaryCancellationRef = ref<HTMLElement | null>(null)
  const cancellationArticleRef = ref<HTMLElement | null>(null)

  const scrollTo = (el: HTMLElement | null) => {
    setTimeout(() => {
      el?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, 100)
  }

  watch(primaryChoice, (newVal, oldVal) => {
    if (newVal !== oldVal) {
      scrollTo(secondaryCancellationRef.value)
    }
    otherSupplier.value = ""
    secondaryChoice.value = ""
  })

  watch(secondaryCancellationRef, (newVal) => {
    if (newVal !== null) {
      scrollTo(secondaryCancellationRef.value)
    }
  })

  watch(secondaryChoice, (newVal, oldVal) => {
    if (newVal !== oldVal) {
      scrollTo(cancellationArticleRef.value)
    }
  })

  watch(cancellationArticleRef, (newVal, oldVal) => {
    if (newVal !== oldVal) {
      scrollTo(cancellationArticleRef.value)
    }
    otherSupplier.value = ""
  })

  const optionArticle = computed(() => {
    return reasonOptions.value.find(
      (option) => option.value === secondaryChoice.value,
    )
  })
  const reasonOptions = computed(() => {
    const reason = cancellationReasons.find(
      (option) => option.value === primaryChoice.value,
    )
    return reason?.reasons || []
  })

  const cheaperTariffProvider = computed(() => {
    if (otherSupplier.value) return " - " + otherSupplier.value
    else return ""
  })
  const cancellationReasonString = computed(() => {
    return secondaryChoice.value + cheaperTariffProvider.value
  })

  const {
    mutateAsync: submitCancelSwitch,
    isPending: cancelSwitchPending,
    isError: cancelSwitchError,
  } = useCancelSwitchV2()

  const {
    mutateAsync: submitCancellationReason,
    isPending: cancellationReasonPending,
    isError: cancellationReasonError,
  } = useCancelSwitchReasons()

  const onSubmit = handleSubmit(async () => {
    submitCancelSwitch(
      {
        accountId: accountIdQuery.accountId.value,
        data: {
          cancellationReason: cancellationReasonString.value,
        },
      },
      {
        onSuccess: () => {
          for (const meterPoint of metersQuery?.data?.value || []) {
            submitCancellationReason({
              accountId: accountIdQuery.accountId.value,
              data: {
                channel: SoChannel.MyAccount,
                meterPointId: meterPoint.meterpointID,
                reasonDetail: cancellationReasonString.value,
              },
            })
          }
          emit("update:success", true)
        },
      },
    )
  })
</script>

<template>
  <DialogHeader>
    <DialogTitle>Cancel your switch</DialogTitle>
  </DialogHeader>

  <form class="overflow-y-auto" @submit="onSubmit">
    <div class="pb-6">
      <div class="flex flex-col gap-6">
        <div>
          <p>
            We're sorry to hear you'd like to cancel your switch to us. If this
            was a mistake, you can
            <DialogClose as-child>
              <a href="/account" class="text-primary-600 underline"
                >return to your account</a
              > </DialogClose
            >. If not, please select a reason for your cancellation and confirm
            your choice.
          </p>
        </div>
        <Select
          v-model="primaryChoice"
          :class="'max-w-[272px] sm:max-w-[320px]'"
          name="primaryCancellationReason"
          :items="cancellationReasons"
          label="Please select a reason for your cancellation"
          placeholder="Select a reason"
          avoid-collisions
          data-testid="primary-reason"
          validate-on-model-update
        />
      </div>
      <div
        v-if="primaryChoice"
        ref="secondaryCancellationRef"
        class="mt-6 flex flex-col gap-6"
      >
        <p>
          We value your feedback and strive to improve. Please share your
          experience before we cancel your switch.
        </p>
        <Select
          v-model="secondaryChoice"
          :class="'max-w-[272px] sm:max-w-[320px]'"
          name="secondaryCancellationReason"
          :items="reasonOptions"
          label="Select a reason"
          placeholder="Select a reason"
          :show-label="false"
          avoid-collisions
          data-testid="secondary-reason"
          validate-on-model-update
        />
        <div
          v-if="secondaryChoice"
          ref="cancellationArticleRef"
          class="flex flex-col gap-6"
        >
          <div class="space-y-6">
            <component :is="optionArticle?.article" />
          </div>
          <Select
            v-if="optionArticle?.suppliers"
            v-model="otherSupplier"
            :class="'max-w-[272px] sm:max-w-[320px]'"
            :items="suppliers"
            name="otherSupplier"
            label="Confirm your new supplier"
            placeholder="Confirm your new supplier"
            :show-label="false"
            avoid-collisions
            data-testid="new-supplier"
            validate-on-model-update
          />
        </div>
      </div>
      <Alert
        v-if="cancelSwitchError || cancellationReasonError"
        data-testid="error-dialog"
        variant="error"
        class="mt-6"
      >
        <AlertDescription>
          Something went wrong. Please try again later.
        </AlertDescription>
      </Alert>
    </div>

    <DialogFooter>
      <DialogClose as-child>
        <Button variant="outline">Cancel</Button>
      </DialogClose>
      <Button
        type="submit"
        variant="primary"
        :loading="cancelSwitchPending || cancellationReasonPending"
        data-testid="confirm-cancellation-btn"
      >
        Confirm Cancellation
      </Button>
    </DialogFooter>
  </form>
</template>
